import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, EnvelopeSimple, CheckCircle, ArrowLeft } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import Header from "../components/Header";
import Footer from "../components/Footer";
import WhatsAppButton from "../components/WhatsAppButton";

// Video URL
const TREPPEN_VIDEO = "https://customer-assets.emergentagent.com/job_9cba13cf-f2db-4d29-a3e0-dbd97dd97a4f/artifacts/9gxzylto_f1f97e81-284e-4a78-8084-1a8be662e1f0.mov";

const TreppenPage = () => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleVideoPlay = () => setIsPlaying(true);
  const handleVideoPause = () => setIsPlaying(false);

  const features = [
    "Fugenlose Designflächen",
    "Marmor-Look & individuelle Farben",
    "Glanz- oder Mattversiegelung",
    "360° Komplett-Beschichtung",
    "Langlebig & pflegeleicht",
    "Umsetzung in 2-5 Werktagen"
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-slate-900 to-slate-800" data-testid="treppen-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-slate-400 hover:text-white mb-8 transition-colors"
            data-testid="back-link"
          >
            <ArrowLeft size={20} className="mr-2" />
            Zurück zur Startseite
          </Link>
          
          <div className="text-center">
            <span className="font-body text-amber-500 font-semibold uppercase tracking-wider text-sm">
              Neu bei Pure Epox
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white mt-4 mb-6">
              Epoxid Treppen Beschichtung
            </h1>
            <p className="font-body text-slate-300 text-lg max-w-3xl mx-auto">
              Verleihen Sie Ihrer Treppe ein modernes Erscheinungsbild – hochwertig, langlebig und individuell.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white" data-testid="treppen-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            
            {/* Text Content */}
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-slate-900 mb-8">
                Ihre Treppe neu gedacht
              </h2>
              
              <div className="space-y-6 font-body text-slate-600 text-lg leading-relaxed">
                <p>
                  Verleihen Sie Ihrer Treppe ein neues, modernes Erscheinungsbild – ganz ohne Austausch. 
                  Mit unserer hochwertigen Epoxidharz- und Polyurethan-Beschichtung entstehen fugenlose 
                  Designflächen ganz nach Ihren Wünschen: von elegantem Marmor-Look bis hin zu 
                  individuellen Farbgestaltungen.
                </p>
                <p>
                  Wählen Sie zwischen edler Glanz- oder dezenter Mattversiegelung für den perfekten Finish. 
                  Ihre Treppe wird vollständig (360°) beschichtet und erhält so ein einheitliches, 
                  hochwertiges Gesamtbild.
                </p>
                <p>
                  Die Beschichtung ist langlebig, pflegeleicht und eine kostengünstige Alternative 
                  zur neuen Treppe – <span className="font-semibold text-slate-900">bereits ab 1.500 €</span>. 
                  Die Umsetzung erfolgt schnell und effizient, meist innerhalb von 2 bis 5 Werktagen.
                </p>
                <p className="font-semibold text-slate-900 text-xl">
                  Machen Sie Ihre Treppe zu einem echten Unikat.
                </p>
              </div>

              {/* Features List */}
              <div className="mt-10 grid grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle size={20} weight="fill" className="text-amber-500 flex-shrink-0" />
                    <span className="font-body text-slate-700 text-sm">{feature}</span>
                  </div>
                ))}
              </div>

              {/* Price Highlight */}
              <div className="mt-10 bg-slate-50 rounded-sm p-6 border-l-4 border-amber-500">
                <p className="font-body text-slate-600 text-sm uppercase tracking-wider mb-1">
                  Bereits ab
                </p>
                <p className="font-heading text-4xl font-bold text-slate-900">
                  1.500 €
                </p>
                <p className="font-body text-slate-500 text-sm mt-1">
                  inkl. Material & Arbeitszeit
                </p>
              </div>

              {/* CTA Button */}
              <div className="mt-10">
                <a 
                  href="mailto:pureepox@gmail.com?subject=Kataloganfrage%20–%20Treppenbeschichtung"
                  data-testid="katalog-cta"
                >
                  <Button className="btn-gold text-white rounded-sm font-bold uppercase tracking-wide px-10 py-7 h-auto text-lg">
                    <EnvelopeSimple size={24} weight="bold" className="mr-3" />
                    Katalog anfragen
                  </Button>
                </a>
              </div>
            </div>

            {/* Video Section */}
            <div className="lg:sticky lg:top-32">
              <div 
                className="relative rounded-sm overflow-hidden shadow-2xl bg-slate-900"
                data-testid="treppen-video-container"
              >
                <video
                  ref={videoRef}
                  src={TREPPEN_VIDEO}
                  className="w-full aspect-[9/16] object-cover"
                  playsInline
                  muted
                  loop
                  onPlay={handleVideoPlay}
                  onPause={handleVideoPause}
                  data-testid="treppen-video"
                />
                
                {/* Play/Pause Overlay */}
                <button
                  onClick={togglePlay}
                  className={`absolute inset-0 flex items-center justify-center bg-black/30 transition-opacity duration-300 ${isPlaying ? 'opacity-0 hover:opacity-100' : 'opacity-100'}`}
                  data-testid="video-play-button"
                >
                  <div className="w-20 h-20 bg-white/90 rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors">
                    {isPlaying ? (
                      <Pause size={36} weight="fill" className="text-slate-900" />
                    ) : (
                      <Play size={36} weight="fill" className="text-slate-900 ml-1" />
                    )}
                  </div>
                </button>
              </div>
              
              <p className="font-body text-slate-500 text-sm text-center mt-4">
                Video: Beispiel einer Treppen-Beschichtung
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900" data-testid="treppen-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-white mb-6">
            Interesse geweckt?
          </h2>
          <p className="font-body text-slate-300 text-lg mb-10 max-w-2xl mx-auto">
            Fordern Sie jetzt unseren kostenlosen Katalog an und lassen Sie sich von unseren 
            Treppen-Designs inspirieren. Wir beraten Sie gerne!
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a 
              href="mailto:pureepox@gmail.com?subject=Kataloganfrage%20–%20Treppenbeschichtung"
            >
              <Button className="btn-gold text-white rounded-sm font-bold uppercase tracking-wide px-10 py-6 h-auto w-full">
                <EnvelopeSimple size={20} weight="bold" className="mr-2" />
                Katalog anfragen
              </Button>
            </a>
            <a 
              href="https://wa.me/4917672636169?text=Hallo%2C%20ich%20interessiere%20mich%20für%20eine%20Treppen-Beschichtung."
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 rounded-sm font-semibold uppercase tracking-wide px-10 py-6 h-auto w-full"
              >
                Per WhatsApp anfragen
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default TreppenPage;
